import { z } from 'zod';

/**
 * Phase métier du lead (orthogonale à `LeadDelivery.status` qui décrit
 * l'état technique du dispatch en cours). Modèle à 8 états validé le
 * 2026-05-18. Transitions clés :
 *
 *   created ─┬─ TEMPORISATION OK ─▶ scheduled
 *            ├─ DIRECT_SEND OK ───▶ in_delivery
 *            └─ consent_expired / manual ─▶ cancelled
 *   scheduled ─ slot atteint ─▶ in_delivery
 *   in_delivery ─┬─ ack partenaire OK ─▶ delivered
 *                └─ KO/quarantaine ───▶ delivery_failed
 *   delivered ─┬─ vente (retour call) ▶ sale_validated ─┬─ confirmation OK ▶ sale_confirmed
 *              │                                          └─ confirmation KO ▶ sale_rejected
 *              └─ non-vente ▶ not_sold
 *
 * Cycle de vente en 2 temps (refonte Cyrille 2026-07) : le retour du call
 * pose `sale_validated` (NON final, en attente) ; la CONFIRMATION (TMK pour le
 * telco, rappro facturation / action admin pour les autres) tranche en
 * `sale_confirmed` (final, seule à générer la commission) ou `sale_rejected`
 * (final). L'ancien `sold` est CONSERVÉ (valeur enum Postgres non supprimable)
 * mais DÉPRÉCIÉ : plus jamais écrit après le backfill. Cf. le doc de refonte.
 */
export const LeadStatus = {
  /** Initial : push Collect lancé, pas encore d'écho. */
  created: 'created',
  /** TEMPORISATION OK côté Collect, en attente du slot. */
  scheduled: 'scheduled',
  /** DIRECT_SEND OK ou slot atteint : push actif vers le partenaire. */
  in_delivery: 'in_delivery',
  /** Ack partenaire OK (exact_response=OK BQ ou Hopti delivered-sync). */
  delivered: 'delivered',
  /** Tentative réelle, échec terminal système (quarantine, KO partenaire…). */
  delivery_failed: 'delivery_failed',
  /** Livraison jamais tentée (consent_expired, admin/vendor manuel). Terminal. */
  cancelled: 'cancelled',
  /**
   * @deprecated Ancien statut de vente unique. Remplacé par le cycle
   * `sale_validated` → `sale_confirmed`/`sale_rejected`. Conservé pour la
   * rétrocompat des données pré-refonte (l'enum Postgres ne se supprime pas) ;
   * plus jamais écrit. Traité comme une vente validée dans les affichages.
   */
  sold: 'sold',
  /** Qualification finale ✗ — pas de vente après contact (le call dit « non »). */
  not_sold: 'not_sold',
  /** Vente remontée par le call, PAS encore confirmée. NON final (en attente). */
  sale_validated: 'sale_validated',
  /**
   * Vente ACTIVÉE (TMK OK / rappro / admin). FINAL — seule à générer la
   * commission. Libellé UI : « Vente activée » — terminologie des contrats
   * partenaires (2026-09). Le nom de la valeur reste `sale_confirmed` : c'est
   * une valeur d'enum Postgres, non renommable sans un ALTER TYPE qui
   * bloquerait les déploiements. Ne pas « harmoniser ».
   */
  sale_confirmed: 'sale_confirmed',
  /** Vente rejetée à la confirmation (TMK tout-KO / rappro KO / admin). FINAL. */
  sale_rejected: 'sale_rejected',
} as const;

export type LeadStatus = (typeof LeadStatus)[keyof typeof LeadStatus];

export const LeadStatusSchema = z.nativeEnum(LeadStatus);

/**
 * Statuts considérés comme **terminaux côté business** : une fois qu'un lead
 * y entre, son statut ne peut plus être modifié automatiquement (qualifs
 * callcenter ultérieures, picker admin, etc.). Les modifications ne sont
 * possibles que par PATCH /leads/:id explicite avec un admin authentifié,
 * et chaque tentative est validée côté serveur via `isTerminalStatus()`.
 *
 * Pour l'instant on ne marque que `sold` comme terminal :
 *  - Demande métier Cyrille (mail 25/06) : un statut Vente forcé à la main
 *    par l'admin ne doit JAMAIS être réécrit par les retours qualif
 *    callcenter ultérieurs (qui peuvent dire "Refus argumenté" alors que
 *    la vente est faite — vu sur 6 leads Telco Club Conso).
 *  - Les qualifs callcenter postérieures restent ingérées en audit-trail
 *    (LeadEvent adminOnly=true) ; elles ne peuvent simplement plus modifier
 *    le statut de la fiche.
 *
 * Refonte 2026-07 : toutes les phases de vente sont désormais terminales
 * vis-à-vis des qualifs automatiques — dès qu'une vente est VALIDÉE (retour
 * call), une qualif d'appel ultérieure (ex. codification système « injoignable »
 * tardive) ne doit plus pouvoir la faire retomber en « Non vente » (décision
 * Cyrille : une vente validée est protégée ; seule la CONFIRMATION — TMK/admin —
 * ou une action admin explicite la change). `sold` (déprécié) reste terminal
 * pour les éventuelles fiches pré-refonte non backfillées.
 */
export const TERMINAL_LEAD_STATUSES: readonly LeadStatus[] = [
  'sale_validated',
  'sale_confirmed',
  'sale_rejected',
  'sold',
] as const;

/**
 * Helper de vérification : `true` si le statut donné fait partie de
 * `TERMINAL_LEAD_STATUSES`. Utilisé côté serveur pour gater les
 * transitions automatiques, et côté UI pour désactiver visuellement
 * les boutons qui changeraient le statut.
 */
export function isTerminalStatus(status: LeadStatus | string): boolean {
  return TERMINAL_LEAD_STATUSES.includes(status as LeadStatus);
}

/**
 * Statuts « finalisés » = la fiche a une ISSUE posée (vente, non-vente,
 * annulation). Distinct de `TERMINAL_LEAD_STATUSES` (qui ne verrouille QUE
 * `sold` contre les qualifs auto) : ici on regroupe toutes les issues pour
 * décider si une RE-qualification tardive (relabel système, ex.
 * `sys_unreachable_limit`) a encore le droit de basculer le statut.
 *
 * Règle : un relabel ne referme le lead QUE s'il est encore OUVERT (pas
 * finalisé) — ça permet à une clôture système d'aboutir sur une fiche restée
 * « livrée », sans rouvrir ni faire osciller une fiche qui a déjà son issue.
 */
export const FINALIZED_LEAD_STATUSES: readonly LeadStatus[] = [
  'sale_confirmed',
  'sale_rejected',
  'not_sold',
  'cancelled',
  'sold',
] as const;

/** `true` si la fiche a déjà une issue posée (cf. `FINALIZED_LEAD_STATUSES`). */
export function isFinalizedStatus(status: LeadStatus | string): boolean {
  return FINALIZED_LEAD_STATUSES.includes(status as LeadStatus);
}

/**
 * Toutes les phases « vente » (validée / confirmée / refusée + `sold` déprécié).
 * Sert au regroupement (funnel, filtres) et à détecter qu'une fiche est sur le
 * parcours de vente. `sale_validated` est une vente NON encore acquise.
 */
export const SALE_STATUSES: readonly LeadStatus[] = [
  'sale_validated',
  'sale_confirmed',
  'sale_rejected',
  'sold',
] as const;

/** `true` si le statut est une phase de vente (cf. `SALE_STATUSES`). */
export function isSaleStatus(status: LeadStatus | string): boolean {
  return SALE_STATUSES.includes(status as LeadStatus);
}

/**
 * Statuts « vente acquise » = ceux qui comptent au CA et déclenchent la
 * commission. Refonte : SEULE la vente confirmée. Point d'ancrage unique
 * consommé par le dashboard/reporting (remplace l'ancien `['sold']`).
 * `sold` (déprécié) y est inclus en filet de sécurité pour d'éventuelles
 * fiches pré-refonte non backfillées — à retirer une fois le backfill certifié.
 */
export const COMMISSIONABLE_SALE_STATUSES: readonly LeadStatus[] = [
  'sale_confirmed',
  'sold',
] as const;

/** `true` si la vente est acquise (compte au CA / commission). */
export function isCommissionableStatus(status: LeadStatus | string): boolean {
  return COMMISSIONABLE_SALE_STATUSES.includes(status as LeadStatus);
}

/**
 * Raison de non-vente (status=`not_sold`), motivée côté livraison (Hopti)
 * ou côté partenaire. Visible UNIQUEMENT par les admins
 * (cf. AnonymizeInterceptor). On démarre avec une seule valeur.
 */
/**
 * Statuts où la fiche est encore **entre les mains du centre d'appel** : il
 * peut rappeler le prospect, et le vendeur peut encore le prévenir (story
 * « à prévenir », SMS préétabli). Vendue, refusée, annulée ou en échec de
 * livraison, plus personne n'appelle — on n'écrit plus non plus. Une seule
 * frontière, lue par l'API (stories, quota SMS) et par l'UI (bouton).
 */
export const CALLCENTER_IN_PROGRESS_STATUSES: ReadonlySet<LeadStatus> = new Set<LeadStatus>([
  'created',
  'scheduled',
  'in_delivery',
  'delivered',
]);

export function isCallcenterInProgress(status: LeadStatus | string): boolean {
  return CALLCENTER_IN_PROGRESS_STATUSES.has(status as LeadStatus);
}

export const NonVenteReason = {
  /** Lead rejeté côté partenaire de livraison (doublon, hors zone, etc.). */
  ko_partenaire: 'ko_partenaire',
} as const;

export type NonVenteReason = (typeof NonVenteReason)[keyof typeof NonVenteReason];

export const NonVenteReasonSchema = z.nativeEnum(NonVenteReason);

/**
 * Raison pour laquelle un lead est passé en `cancelled` (livraison jamais
 * tentée). Admin-only à l'affichage.
 */
export const CancellationReason = {
  /** OTP consent différé non saisi dans les délais. Cas v1 majoritaire. */
  consent_expired: 'consent_expired',
  /** Action manuelle admin. */
  admin_manual: 'admin_manual',
  /** Action manuelle vendor / superviseur. */
  vendor_manual: 'vendor_manual',
} as const;

export type CancellationReason = (typeof CancellationReason)[keyof typeof CancellationReason];

export const CancellationReasonSchema = z.nativeEnum(CancellationReason);

/**
 * Raison pour laquelle une tentative de livraison a échoué
 * (status=`delivery_failed`). Admin-only à l'affichage.
 */
export const DeliveryFailedReason = {
  /** Quarantaine Hopti après échec WS partenaire. */
  quarantine_hopti: 'quarantine_hopti',
  /** Erreur WS (timeout, 5xx) après retries épuisés. */
  partner_ws_error: 'partner_ws_error',
  /** Réponse partenaire `KO` côté BQ (`outgoing_data_exact_response`). */
  partner_refused: 'partner_refused',
  /**
   * Le webservice de l'acheteur a refusé la fiche parce qu'il la connaît
   * déjà (doublon). Ce n'est pas une panne : la fiche a bien été transmise,
   * l'acheteur l'a simplement déjà. Détecté sur la réponse du WS (motif
   * `duplicateResult` côté Hopti, cf. `classifyDeliveryFailure` dans l'API).
   */
  partner_duplicate: 'partner_duplicate',
} as const;

export type DeliveryFailedReason = (typeof DeliveryFailedReason)[keyof typeof DeliveryFailedReason];

export const DeliveryFailedReasonSchema = z.nativeEnum(DeliveryFailedReason);

/**
 * Libellés FR des raisons d'échec, vus par l'ADMIN (timeline, export, Slack).
 * Les non-admins ne voient pas la raison technique — sauf le doublon, qui
 * leur est présenté comme une issue (« Doublon », cf. `isBuyerDuplicate`).
 */
export const DELIVERY_FAILED_REASON_LABEL_FR: Record<DeliveryFailedReason, string> = {
  quarantine_hopti: 'Quarantaine Hopti — lead non livré',
  partner_ws_error: 'Erreur webservice partenaire',
  partner_refused: 'Refus partenaire (KO webservice)',
  partner_duplicate: "Doublon chez l'acheteur",
};

/**
 * `true` quand une fiche en échec de livraison l'est parce que l'acheteur
 * l'avait déjà (doublon) — c'est une issue métier, pas une panne : l'UI la
 * présente comme « Doublon » plutôt que « Échec livraison ».
 */
export function isBuyerDuplicate(
  status: LeadStatus | string,
  deliveryFailedReason: string | null | undefined,
): boolean {
  return status === 'delivery_failed' && deliveryFailedReason === 'partner_duplicate';
}
